/**
 * Shared Stock Grid - replaces DistributionRiskTable, AccumulationCandidateTable, TransitionStockTable
 * CSS grid layout, compact rows, shared logic
 */
import React from 'react';
import { Box, Typography, Chip, Tooltip, alpha, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { renderVolumeChange, getTagColor, translateTag } from './utils';

// ── Shared row template ──────────────────────
// Ticker | Sector | % đỉnh | Giá | Vol | Score(s) | Signals | Action
const COLS_DIST = '70px 1fr 65px 65px 65px 110px 1.5fr 70px';
const COLS_ACCUM = '70px 1fr 65px 65px 65px 110px 1.5fr 70px';
const COLS_TRANS = '70px 1fr 65px 65px 65px 90px 90px 1.2fr 70px';

const headStyle = {
  fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1,
};

const cellStyle = { display: 'flex', alignItems: 'center', minHeight: 0, overflow: 'hidden' };

const ScoreBar = ({ score, color, bgColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
    <Box sx={{ flex: 1, height: 5, bgcolor: bgColor, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${Math.min(score, 100)}%`, bgcolor: color, borderRadius: 2 }} />
    </Box>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color, fontFamily: 'monospace', minWidth: 22, textAlign: 'right' }}>
      {score?.toFixed(0)}
    </Typography>
  </Box>
);

const StockGrid = ({ data, loading, error, variant = 'distribution' }) => {
  const { t, i18n } = useTranslation();

  const isTransition = variant === 'transition';
  const cols = isTransition ? COLS_TRANS : (variant === 'distribution' ? COLS_DIST : COLS_ACCUM);

  const themeColor = variant === 'distribution' ? '#dc2626'
    : variant === 'accumulation' ? '#16a34a' : '#ca8a04';

  const hoverBg = variant === 'distribution' ? '#fef2f2'
    : variant === 'accumulation' ? '#f0fdf4' : '#fefce8';

  if (loading) {
    return (
      <Box>
        {/* Skeleton header */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: cols, gap: 1, px: 1.5, py: 0.8,
          borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc',
        }}>
          {(isTransition ? [40, '1fr', 35, 35, 35, 50, 50, '1fr', 45] : [40, '1fr', 35, 35, 35, 60, '1fr', 45]).map((_, i) => (
            <Skeleton key={i} variant="text" width={typeof _ === 'number' ? _ : '60%'} height={10} sx={{ borderRadius: 0.5 }} />
          ))}
        </Box>
        {/* Skeleton rows */}
        {[...Array(8)].map((_, ri) => (
          <Box key={ri} sx={{
            display: 'grid', gridTemplateColumns: cols, gap: 1,
            px: 1.5, py: 0.9, alignItems: 'center',
            borderBottom: '1px solid #f1f5f9',
            borderLeft: `3px solid ${alpha(themeColor, 0.15)}`,
          }}>
            {/* Ticker */}
            <Box>
              <Skeleton variant="text" width={42} height={14} sx={{ borderRadius: 0.5, mb: 0.2 }} />
              <Skeleton variant="text" width={30} height={8} sx={{ borderRadius: 0.5 }} />
            </Box>
            {/* Sector */}
            <Skeleton variant="text" width="70%" height={12} sx={{ borderRadius: 0.5 }} />
            {/* % peak */}
            <Skeleton variant="text" width={36} height={14} sx={{ borderRadius: 0.5, ml: 'auto' }} />
            {/* Price */}
            <Skeleton variant="text" width={32} height={14} sx={{ borderRadius: 0.5, ml: 'auto' }} />
            {/* Vol */}
            <Skeleton variant="text" width={28} height={14} sx={{ borderRadius: 0.5, ml: 'auto' }} />
            {/* Score(s) */}
            {isTransition ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="rounded" height={5} sx={{ flex: 1, borderRadius: 1 }} />
                  <Skeleton variant="text" width={18} height={12} sx={{ borderRadius: 0.5 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="rounded" height={5} sx={{ flex: 1, borderRadius: 1 }} />
                  <Skeleton variant="text" width={18} height={12} sx={{ borderRadius: 0.5 }} />
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Skeleton variant="rounded" height={5} sx={{ flex: 1, borderRadius: 1 }} />
                <Skeleton variant="text" width={18} height={12} sx={{ borderRadius: 0.5 }} />
              </Box>
            )}
            {/* Signals */}
            <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
              {[28, 36, 24].slice(0, 1 + (ri % 3)).map((w, si) => (
                <Skeleton key={si} variant="rounded" width={w} height={16} sx={{ borderRadius: 1 }} />
              ))}
            </Box>
            {/* Action */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Skeleton variant="rounded" width={44} height={18} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#ef4444' }}>{error}</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 500 }}>
          {variant === 'distribution'
            ? t('downshift.distribution_risk.no_results', 'Không tìm thấy mã phân phối')
            : variant === 'accumulation'
            ? t('downshift.accumulation.no_results', 'Không tìm thấy mã tích lũy')
            : t('downshift.transition.no_results', 'Không tìm thấy mã chuyển giao')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header row */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: cols, gap: 1, px: 1.5, py: 0.8,
        borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc',
      }}>
        <Typography sx={headStyle}>{t('downshift.table.ticker', 'Mã')}</Typography>
        <Typography sx={headStyle}>{t('downshift.table.sector', 'Ngành')}</Typography>
        <Typography sx={{ ...headStyle, textAlign: 'right' }}>% đỉnh</Typography>
        <Typography sx={{ ...headStyle, textAlign: 'right' }}>{t('downshift.table.price', 'Giá')}</Typography>
        <Typography sx={{ ...headStyle, textAlign: 'right' }}>Vol Δ</Typography>
        {isTransition ? (
          <>
            <Typography sx={{ ...headStyle, textAlign: 'right' }}>Đ. Phân phối</Typography>
            <Typography sx={{ ...headStyle, textAlign: 'right' }}>Đ. Tích lũy</Typography>
          </>
        ) : (
          <Typography sx={{ ...headStyle, textAlign: 'right' }}>
            {variant === 'distribution'
              ? t('downshift.distribution_risk.score', 'Điểm PP')
              : t('downshift.accumulation.score', 'Điểm TL')}
          </Typography>
        )}
        <Typography sx={headStyle}>{t('downshift.table.signals', 'Tín hiệu')}</Typography>
        <Typography sx={{ ...headStyle, textAlign: 'center' }}>{t('downshift.table.action', 'Hành động')}</Typography>
      </Box>

      {/* Data rows */}
      {data.map((item, idx) => (
        <Box
          key={item.ticker || idx}
          sx={{
            display: 'grid', gridTemplateColumns: cols, gap: 1,
            px: 1.5, py: 0.8, alignItems: 'center',
            borderBottom: '1px solid #f1f5f9',
            borderLeft: `3px solid ${themeColor}`,
            transition: 'background 0.15s',
            '&:hover': { bgcolor: hoverBg },
            '&:nth-of-type(even)': { bgcolor: '#fafbfc' },
            '&:nth-of-type(even):hover': { bgcolor: hoverBg },
          }}
        >
          {/* Ticker */}
          <Box sx={cellStyle}>
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: themeColor, lineHeight: 1.2, letterSpacing: 0.3 }}>
                {item.ticker}
              </Typography>
              <Typography sx={{ fontSize: '0.52rem', color: '#94a3b8', fontWeight: 500 }}>
                {item.regime}
              </Typography>
            </Box>
          </Box>

          {/* Sector */}
          <Box sx={{ ...cellStyle, overflow: 'hidden' }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 500, color: '#475569',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {item.sector_name ? item.sector_name[i18n.language] : '-'}
            </Typography>
          </Box>

          {/* % of High 120 */}
          <Box sx={{ ...cellStyle, justifyContent: 'flex-end' }}>
            <Typography sx={{
              fontSize: '0.78rem', fontWeight: 800, fontFamily: 'monospace',
              color: variant === 'distribution'
                ? (item.pct_of_high_120 >= 95 ? '#dc2626' : '#ea580c')
                : variant === 'accumulation'
                ? (item.pct_of_high_120 <= 60 ? '#16a34a' : '#ca8a04')
                : '#ca8a04',
            }}>
              {item.pct_of_high_120?.toFixed(1)}%
            </Typography>
          </Box>

          {/* Price */}
          <Box sx={{ ...cellStyle, justifyContent: 'flex-end' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', fontFamily: 'monospace' }}>
              {(item.close / 1000).toFixed(1)}k
            </Typography>
          </Box>

          {/* Volume Change */}
          <Box sx={{ ...cellStyle, justifyContent: 'flex-end' }}>
            {renderVolumeChange(item.volume, item.avg_value_20, item.close, t)}
          </Box>

          {/* Score(s) */}
          {isTransition ? (
            <>
              <Box sx={{ ...cellStyle }}>
                <ScoreBar score={item.distribution_score} color="#ea580c" bgColor="#fee2e2" />
              </Box>
              <Box sx={{ ...cellStyle }}>
                <ScoreBar score={item.capitulation_score} color="#84cc16" bgColor="#dcfce7" />
              </Box>
            </>
          ) : (
            <Box sx={{ ...cellStyle }}>
              <ScoreBar
                score={variant === 'distribution' ? item.distribution_score : item.capitulation_score}
                color={variant === 'distribution' ? '#dc2626' : '#16a34a'}
                bgColor={variant === 'distribution' ? '#fee2e2' : '#dcfce7'}
              />
            </Box>
          )}

          {/* Signals */}
          <Box sx={{ ...cellStyle, flexWrap: 'wrap', gap: 0.3 }}>
            {(item.tags || '').split(' ').filter(Boolean).map((tag, tidx) => {
              const cleanTag = tag.replace(/[>=<]/g, '').split('=')[0];
              const color = getTagColor(cleanTag);
              return (
                <Tooltip key={tidx} title={tag} placement="top" arrow>
                  <Chip
                    label={translateTag(tag, t)}
                    size="small"
                    sx={{
                      height: 18, fontSize: '0.58rem', fontWeight: 600,
                      bgcolor: alpha(color, 0.1), color,
                      border: `1px solid ${alpha(color, 0.2)}`,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>

          {/* Action */}
          <Box sx={{ ...cellStyle, justifyContent: 'center' }}>
            <Box sx={{
              display: 'inline-flex', px: 0.8, py: 0.25, borderRadius: 1,
              bgcolor: alpha(themeColor, 0.1), border: `1px solid ${alpha(themeColor, 0.2)}`,
            }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: themeColor, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                {item.action}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}

      {/* Footer count */}
      <Box sx={{ px: 1.5, py: 0.6, bgcolor: '#fafbfc', borderTop: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>
          {data.length} {t('downshift.filters.stocks', 'mã')}
        </Typography>
      </Box>
    </Box>
  );
};

export default StockGrid;
